import Page from '@pelckmans/elementis/components/page';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams } from 'react-router-dom';
import IdentityContext from '../../../contexts/IdentityContext';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import ContentBody from '../../../layout/content/ContentBody';
import ContentHeader from '../../../layout/content/ContentHeader';
import ModuleAlerts from '../../../shared-components/alerts/ModuleAlerts';
import {
  withScrollThreshold,
  WithScrollThresholdProps,
} from '../../../shared-components/elementis/hoc/withScrollThreshold';
import ModuleContext from '../_contexts/ModuleContext';
import TocDataProvider from './_contexts/TocDataProvider';
import ActionBar from './components/ActionBar';
import ExerciseInstructionSection from './components/ExerciseInstructionSection';
import ExerciseNodeCardSection from './components/ExerciseNodeCardSection';
import PinnedMaterialSection from './components/pinned-material/PinnedMaterialSection';
import StudentMaterialSections from './components/student/MaterialSections';
import TeacherMaterialSection from './components/teacher/MaterialSection';
import TocPageHeader from './components/toc-page-header';
import TocMarketingNotifications from './components/TocMarketingNotifications';

interface TocProps extends WithScrollThresholdProps {}

export function Toc({ pastThreshold }: TocProps) {
  const { t } = useTranslation();

  useDocumentTitle(t('PAGES.TOC_LEVEL.DOCUMENT_TITLE'));

  const { selectedModule: module, module: superModule, subModules } = useContext(ModuleContext);
  const { identity } = useContext(IdentityContext);
  const { selectedModuleSlug } = useParams();
  const studioGroup = process.env.REACT_APP_P_STUDIO_GROUP;

  if (!superModule) return null;

  const isBAOStudent = studioGroup === 'BAO' && identity.highestRole === 'student';
  const isTryingToAccessSubModule = selectedModuleSlug !== superModule?.slug;
  const hasAccessToModule = isTryingToAccessSubModule
    ? subModules.some(subModule => subModule.slug === module?.slug && subModule.licenses.hasDocumentLicenseForStudent)
    : superModule?.licenses.hasDocumentLicenseForStudent;

  if (isBAOStudent && !hasAccessToModule) {
    return <Navigate to="/" />;
  }
  return (
    <Page>
      {module && <TocMarketingNotifications moduleId={module.id} />}
      <ContentHeader cover={<TocPageHeader density={pastThreshold ? 'compact' : 'normal'} />} />
      <ContentBody alerts={module ? <ModuleAlerts module={module} /> : null}>
        <ExerciseNodeCardSection />
        <ExerciseInstructionSection />
        <PinnedMaterialSection />
        {identity.isTeacherAlike ? <TeacherMaterialSection /> : <StudentMaterialSections />}
      </ContentBody>
      <ActionBar />
    </Page>
  );
}

interface WrappedTocProps extends WithScrollThresholdProps {}

function WrappedToc({ pastThreshold }: WrappedTocProps) {
  return (
    <TocDataProvider>
      <Toc pastThreshold={pastThreshold} />
    </TocDataProvider>
  );
}

export default withScrollThreshold(WrappedToc);
